import React, { useState } from "react";
import InfoSquare, { NoteStrip } from "../../components/InfoSquare.jsx";
import Delta from "./Delta.jsx";
import SkeletonRows from "./Skeleton.jsx";
import SegControl from "../../components/SegControl.jsx";
import {
  DISPLAY_TIMEZONE_LABEL,
  amount2,
  budgetState,
  formatBillingDate,
  formatClock,
  formatQuantity,
  isTodayInDisplayTz,
  money,
  projectRow,
} from "./derive.js";

/**
 * Account -> service -> resource on one screen.
 *
 * This is the whole of the old "By Account" page and the whole of "By
 * Service"'s expand-a-row-at-a-time drill, laid out as three panes so the
 * path from a total to the resource behind it never changes pages. The grid
 * is `auto-fit, minmax(262px, 1fr)`: three panes from about 860px of content
 * width, two below that, one on a phone, with no media query deciding it.
 */
/**
 * Accounts grouped by the account AWS actually invoices for them.
 *
 * Every row on this page is one account's OWN usage — Cost Explorer is always
 * filtered to its LINKED_ACCOUNT. So a payer's row is NOT that payer's
 * invoice: the invoice also covers every member consolidated under it. Reading
 * one against the other is what makes a correct dashboard look wrong, and the
 * group subtotal below is the number that actually ties.
 *
 * A standalone account forms a group of one, where the subtotal and the row
 * are the same figure. The header is kept anyway — an auditor should be able
 * to see at a glance which line corresponds to which bill, and an account
 * that quietly has no group header would be the ambiguous case.
 */
function groupByPayer(accounts, untracked = []) {
  const groups = new Map();
  for (const account of accounts) {
    const payerId = account.payer_account_id || account.account_id;
    if (!groups.has(payerId)) {
      groups.set(payerId, {
        payerId,
        // A payer that is not itself registered has no display name here, so
        // fall back to the ID rather than rendering "undefined".
        payerName: account.payer_account_name || (payerId === account.account_id ? account.account_name : payerId),
        accounts: [],
        total: 0,
        previousTotal: 0,
        hasPrevious: false,
      });
    }
    const group = groups.get(payerId);
    group.accounts.push(account);
    group.total += account.total;
    if (account.previous_total !== undefined && account.previous_total !== null) {
      group.previousTotal += account.previous_total;
      group.hasPrevious = true;
    }
  }
  // Spend on a payer's bill from accounts the registry does not know about.
  // It belongs IN the group total — that residual is the difference between
  // what the dashboard can account for and what AWS actually billed, and
  // leaving it out is what made the subtotal read low against the invoice.
  for (const entry of untracked) {
    const group = groups.get(entry.payer_account_id);
    if (!group) continue;
    group.untracked = entry;
    group.total += entry.total;
  }

  return Array.from(groups.values()).sort((a, b) => b.total - a.total);
}

/**
 * A row's projected month end, and how it sits against a budget.
 *
 * The projection scales the row by the whole month's growth factor rather
 * than modelling the row on its own — see projectRow() for why, and the
 * forecast note on screen says so out loud. Rendered only while a month is
 * still running; for a closed month the amount beside it IS the month end.
 */
function Projection({ amount, growth, budget, active }) {
  if (!active) return null;
  const projected = projectRow(amount, growth);
  const state = budgetState(projected, budget);

  if (!state) {
    return <span className="proj-line proj-text">Proj {money(projected)}</span>;
  }
  return (
    <span className="proj-line">
      <span className="budget-bar" aria-hidden="true">
        <span
          className={`budget-fill${state.isOver ? " budget-fill-over" : ""}`}
          style={{ width: `${state.fillPct}%` }}
        />
      </span>
      <span className={`proj-text${state.isOver ? " proj-over" : ""}`}>
        Proj {money(projected)} of {money(state.limit)} ·{" "}
        {state.isOver ? `${money(state.over)} over` : `${Math.round(state.pct)}% of budget`}
      </span>
    </span>
  );
}

/**
 * The readable part of a resource ID. Plain IDs (i-0abc…, vol-…) are already
 * readable; ARNs (RDS, load balancers, …) are cut to their resource part, e.g.
 * "arn:aws:rds:ap-southeast-1:1234:db:e8-oracle-prod" → "e8-oracle-prod".
 */
function resourceLabel(id) {
  if (!id || !id.startsWith("arn:")) return id;
  const resource = id.split(":").slice(5).join(":");
  return resource.replace(/^(db|cluster|snapshot|cluster-snapshot|function|table|secret|key|alias):/, "") || id;
}

function ResourceRows({ resources, onFetch }) {
  const { data, loading, error } = resources || {};
  if (loading) return <SkeletonRows count={4} compact />;
  if (error) return <div className="pane-error">{error}</div>;
  if (!data) return null;
  if (!data.available) {
    return (
      <div className="pane-empty">
        {data.message}
        {data.reason !== "outside_window" && (
          <span className="source-hint">
            Resource IDs need &ldquo;resource-level data at daily granularity&rdquo; switched on for this service in the
            payer account (Billing and Cost Management → Cost Management preferences). It is free, and data appears
            within 48 hours.
          </span>
        )}
      </div>
    );
  }
  const rows = (data.resources || []).filter((r) => Math.abs(Number(r.amount)) >= 0.005);
  return (
    <>
      <div className="res-window">
        Last {data.window_days} days AWS keeps: {formatBillingDate(`${data.window_start}T00:00:00Z`)} to{" "}
        {formatBillingDate(`${data.window_end}T00:00:00Z`)}
      </div>
      {rows.length === 0 && (
        <div className="pane-empty">
          No resource costs in this window.{" "}
          <button type="button" className="link" onClick={onFetch}>
            Fetch from AWS
          </button>
        </div>
      )}
      {rows.map((r) => (
        <div className="usage-row" key={`${r.resource_id ?? "none"}|${r.name_tag ?? ""}`}>
          <span className="pane-line">
            <span className="usage-type res-id" title={r.resource_id || ""}>
              {r.resource_id ? resourceLabel(r.resource_id) : "Not tied to a resource"}
            </span>
            <span className="usage-amount">{amount2(r.amount)}</span>
          </span>
          {r.resource_id && r.name_tag !== resourceLabel(r.resource_id) && (
            <span className="usage-sub">
              {r.name_tag ? (
                <span className="res-tag" title={r.name_tag}>
                  {r.name_tag}
                </span>
              ) : (
                <span className="res-tag res-tag-untagged">no Name tag</span>
              )}
            </span>
          )}
        </div>
      ))}
    </>
  );
}

/**
 * An account with no cost this period is noise in the pane — dozens of
 * sandbox or closed accounts each reading "0.00" push the ones that matter
 * below the fold. Hidden unless: it is the selected account (the URL can
 * point at one), or compare is on and it billed something last period — a
 * drop to zero is itself the news.
 */
const rounds = (n) => Math.abs(Number(n) || 0) >= 0.005;
function isIdle(account, compare) {
  if (rounds(account.total)) return false;
  return !(compare && rounds(account.previous_total));
}

export default function Breakdown({
  accounts,
  accountId,
  onSelectAccount,
  services,
  service,
  onSelectService,
  usageTypes,
  usageLoading,
  usageError,
  onFetchUsageTypes,
  resView,
  onResView,
  resources,
  onFetchResources,
  tagFilter,
  compare,
  cacheInfo,
  growth,
  projecting,
  untracked,
  onExport,
  exportingPayer,
  accountsLoading,
  servicesLoading,
  openNote,
  onToggleNote,
}) {
  const total = accounts.reduce((sum, a) => sum + a.total, 0);
  const selectedAccount = accounts.find((a) => a.account_id === accountId) || null;
  const maxService = services.reduce((max, s) => Math.max(max, s.amount), 0) || 1;
  const selectedService = services.find((s) => s.service === service) || null;

  // Rows that round to nothing in the selected period are dropped rather than
  // shown as zeros: a resource that did not exist yet is not a $0.00 line item.
  const rows = (usageTypes || []).filter((u) => Math.abs(Number(u.amount)) > 0.005);

  const [showIdle, setShowIdle] = useState(false);
  const idleCount = accounts.filter((a) => a.account_id !== accountId && isIdle(a, compare)).length;
  const shown = (a) => showIdle || a.account_id === accountId || !isIdle(a, compare);
  const groups = groupByPayer(accounts, untracked)
    .map((g) => ({ ...g, visible: g.accounts.filter(shown) }))
    .filter((g) => g.visible.length > 0 || g.untracked);

  return (
    <section className="spend-section">
      <div className="section-head section-head-tight">
        <h2>Breakdown</h2>
        <span className="section-meta">
          {selectedAccount ? selectedAccount.account_name : "—"}
          {"  →  "}
          {selectedService ? selectedService.service : "—"}
          {"  →  "}
          {selectedService ? `$${amount2(selectedService.amount)}` : "—"}
        </span>
      </div>
      <p className="section-sub">
        Account, then service, then the resource behind it — one path, no page changes. Accounts are grouped by the
        account AWS bills, so each subtotal matches one invoice.
      </p>

      <div className="breakdown-panes">
        <div className="pane pane-1">
          <div className="pane-head">
            <span className="pane-label">Account</span>
            <span className="pane-label">USD</span>
          </div>
          {accountsLoading && accounts.length === 0 && <SkeletonRows count={3} />}
          {!accountsLoading && accounts.length === 0 && (
            <div className="pane-empty">No account totals for this period yet.</div>
          )}
          {groups.map((group) => (
            <div className="payer-group" key={group.payerId}>
              <div className="payer-head">
                <span className="payer-label">Invoice · {group.payerName}</span>
                <span className="payer-total">{amount2(group.total)}</span>
              </div>
              {/* On its own line rather than in the header: the subtotal has to
                  stay hard right so it lines up with the amounts column below
                  it, and this label is too long to sit beside it in a 262px
                  pane. Named to match the AWS Bills page, which is where the
                  same person does the same job. Per invoice group rather than
                  once for the page — an export is filed against one AWS bill,
                  and a file spanning three unrelated invoices is the wrong
                  unit for a reconciliation. */}
              <div className="payer-actions">
                <button
                  type="button"
                  className="payer-export"
                  onClick={() => onExport(group.payerId, group.payerName)}
                  disabled={exportingPayer === group.payerId}
                  title={`Download every account × service line for ${group.payerName} as CSV`}
                >
                  {exportingPayer === group.payerId ? "Preparing…" : "↓ Download all to CSV"}
                </button>
              </div>
              {group.visible.length > 1 && (
                <div className="payer-note">
                  {group.visible.length} accounts on one bill — the subtotal above is what ties to AWS&apos;s invoice,
                  not any single row below it.
                </div>
              )}
              {/* Indented as a block rather than per row, so the selected
                  row's tint and accent bar keep the geometry they have
                  everywhere else on the page and simply start further in. */}
              <div className="payer-accounts">
                {group.visible.map((a) => {
                  const isSelected = a.account_id === accountId;
                  return (
                    <button
                      key={a.account_id}
                      type="button"
                      className={`pane-row${isSelected ? " selected" : ""}`}
                      aria-pressed={isSelected}
                      onClick={() => onSelectAccount(a.account_id)}
                    >
                      <span className="pane-line">
                        <span className="pane-name">{a.account_name}</span>
                        <span className="pane-amount">{amount2(a.total)}</span>
                      </span>
                      <span className="pane-sub">
                        <span className="pane-account-id">{a.account_id}</span>
                        <Delta current={a.total} previous={a.previous_total} compare={compare} />
                      </span>
                      <span className="share-bar" aria-hidden="true">
                        <span
                          className={`share-fill${isSelected ? " share-fill-selected" : ""}`}
                          style={{ width: `${total ? (a.total / total) * 100 : 0}%` }}
                        />
                      </span>
                      <Projection amount={a.total} growth={growth} budget={a.monthly_budget} active={projecting} />
                    </button>
                  );
                })}

                {group.untracked && (
                  <div className="untracked-row">
                    <span className="pane-line">
                      <span className="pane-name untracked-name">
                        Not registered · {group.untracked.accounts.length}{" "}
                        {group.untracked.accounts.length === 1 ? "account" : "accounts"}
                      </span>
                      <span className="pane-amount">{amount2(group.untracked.total)}</span>
                    </span>
                    <span className="untracked-detail">
                      {group.untracked.accounts.map((a) => a.account_id).join(", ")} — on this bill but missing from the
                      dashboard. Add them on the Accounts page to see what they spend on.
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
          {idleCount > 0 && (
            <div className="pane-idle">
              {showIdle
                ? `Showing ${idleCount} ${idleCount === 1 ? "account" : "accounts"} with no cost this period.`
                : `${idleCount} ${idleCount === 1 ? "account" : "accounts"} with no cost this period hidden.`}{" "}
              <button type="button" className="pane-idle-toggle" onClick={() => setShowIdle((v) => !v)}>
                {showIdle ? "Hide" : "Show"}
              </button>
            </div>
          )}
        </div>

        <div className="pane pane-2">
          <div className="pane-head">
            <span className="pane-label">Service</span>
            <span className="pane-label">USD</span>
          </div>
          {servicesLoading && <SkeletonRows count={5} />}
          {!servicesLoading && services.length === 0 && (
            <div className="pane-empty">No service breakdown for this account and period.</div>
          )}
          {!servicesLoading &&
            services.map((s) => {
            const isSelected = s.service === service;
            return (
              <button
                key={s.service}
                type="button"
                className={`pane-row${isSelected ? " selected" : ""}`}
                aria-pressed={isSelected}
                onClick={() => onSelectService(s.service)}
              >
                <span className="pane-line">
                  <span className="pane-name">{s.service}</span>
                  <span className="pane-amount">{amount2(s.amount)}</span>
                </span>
                <span className="svc-bar-row">
                  {/* Scaled to the largest service in this pane, not the account
                      total — otherwise the long tail is a row of invisible slivers. */}
                  <span className="svc-bar" aria-hidden="true">
                    <span
                      className={`svc-fill${isSelected ? " svc-fill-selected" : ""}`}
                      style={{ width: `${(s.amount / maxService) * 100}%` }}
                    />
                  </span>
                  <Delta current={s.amount} previous={s.previous_amount} compare={compare} />
                </span>
                <Projection amount={s.amount} growth={growth} budget={null} active={projecting} />
              </button>
              );
            })}
        </div>

        <div className="pane pane-3">
          <div className="pane-head">
            <span className="pane-label">
              Resource
              <InfoSquare id="resource" openNote={openNote} onToggle={onToggleNote} label="About resource detail" />
            </span>
            <span className="pane-label">USD</span>
          </div>

          <div className="pane-view">
            <SegControl
              label="Resource view"
              options={[
                { value: false, label: "Usage type" },
                { value: true, label: "Resource ID" },
              ]}
              value={!!resView}
              onChange={onResView}
            />
          </div>

          <NoteStrip id="resource" openNote={openNote}>
            <strong>Usage type</strong> is AWS&apos;s rate code for each charge, for the whole period. Names come from the
            Name cost-allocation tag; usage from before that tag was activated shows as untagged.{" "}
            <strong>Resource ID</strong> shows the instance, database or volume behind the cost. AWS keeps that detail
            for the last 14 days only, and only for services switched on under &ldquo;resource-level data at daily
            granularity&rdquo; in the payer&apos;s Cost Management preferences.
          </NoteStrip>

          {resView && <ResourceRows resources={resources} onFetch={onFetchResources} />}

          {!resView && usageError && <div className="pane-error">{usageError}</div>}
          {!resView && usageLoading && <SkeletonRows count={4} compact />}

          {!resView && !usageLoading && !usageError && rows.length === 0 && (
            <div className="pane-empty">
              No usage-type detail{tagFilter ? " with this tag" : ""} for this service and period.{" "}
              <button type="button" className="link" onClick={onFetchUsageTypes} disabled={!service}>
                Fetch from AWS
              </button>
            </div>
          )}

          {!resView &&
            rows.map((u) => (
            <div className="usage-row" key={`${u.usage_type}|${u.name_tag ?? ""}`}>
              <span className="pane-line">
                <span className="usage-type">{u.usage_type}</span>
                <span className="usage-amount">{amount2(u.amount)}</span>
              </span>
              <span className="usage-sub">
                {u.name_tag ? (
                  <span className="res-tag" title={u.name_tag}>
                    {u.name_tag}
                  </span>
                ) : (
                  <span className="res-tag res-tag-untagged">untagged</span>
                )}
                <span className="res-qty">{formatQuantity(u.quantity, u.unit)}</span>
                <Delta current={u.amount} previous={u.previous_amount} compare={compare} />
              </span>
            </div>
          ))}
        </div>
      </div>

      {cacheInfo && (
        <p className="cache-line">
          Service and usage-type detail cached {formatClock(cacheInfo.cachedAt) || "—"} {DISPLAY_TIMEZONE_LABEL}
          {isTodayInDisplayTz(cacheInfo.cachedAt) ? " today" : ""} from Cost Explorer.{" "}
          {tagFilter
            ? `Filtered to ${tagFilter.key} = ${tagFilter.values.join(", ")}, read live from Cost Explorer.`
            : "Account totals come from the nightly snapshot."}
        </p>
      )}
    </section>
  );
}
